/**
 * Per-conversation queue of reviewer comments the creator has accepted but
 * not yet sent to the Ivy chat. The Ivy composer reads this queue and renders
 * editable chips above the text input. On Send, the chips are bundled with the
 * composer text into a single chat message and the queue is cleared.
 *
 * Anchor-agnostic: items carry a ReviewAnchor (slide or question), so the same
 * queue + chip stack + prompt builder serve both video and assessment reviews.
 */

import { create } from 'zustand';
import type { ReviewAnchor } from '../lib/reviewAnchor';

export interface QueuedReviewItem {
  reviewId: string;
  reviewerName: string;
  /** Server comment id — the unit of identity for the queue. */
  commentId: string;
  /** Original reviewer text. */
  text: string;
  anchor: ReviewAnchor;
  /** Creator's editable note — defaults to the original comment text. */
  creatorNote: string;
}

export type EnqueueInput = Omit<QueuedReviewItem, 'creatorNote'>;

interface State {
  /** Queues are scoped per conversation so switching projects doesn't leak chips. */
  queuesByConversation: Record<string, QueuedReviewItem[]>;

  enqueue(conversationId: string, item: EnqueueInput): void;
  updateNote(conversationId: string, commentId: string, note: string): void;
  remove(conversationId: string, commentId: string): void;
  clear(conversationId: string): void;
  has(conversationId: string, commentId: string): boolean;
}

export const useReviewQueueStore = create<State>((set, get) => ({
  queuesByConversation: {},

  enqueue(conversationId, item) {
    set((state) => {
      const current = state.queuesByConversation[conversationId] ?? [];
      // Idempotent — never queue the same comment twice.
      if (current.some((q) => q.commentId === item.commentId)) return state;
      return {
        queuesByConversation: {
          ...state.queuesByConversation,
          [conversationId]: [...current, { ...item, creatorNote: item.text }],
        },
      };
    });
  },

  updateNote(conversationId, commentId, note) {
    set((state) => {
      const current = state.queuesByConversation[conversationId];
      if (!current) return state;
      return {
        queuesByConversation: {
          ...state.queuesByConversation,
          [conversationId]: current.map((q) => (q.commentId === commentId ? { ...q, creatorNote: note } : q)),
        },
      };
    });
  },

  remove(conversationId, commentId) {
    set((state) => {
      const current = state.queuesByConversation[conversationId];
      if (!current) return state;
      return {
        queuesByConversation: {
          ...state.queuesByConversation,
          [conversationId]: current.filter((q) => q.commentId !== commentId),
        },
      };
    });
  },

  clear(conversationId) {
    set((state) => ({
      queuesByConversation: { ...state.queuesByConversation, [conversationId]: [] },
    }));
  },

  has(conversationId, commentId) {
    const current = get().queuesByConversation[conversationId];
    return !!current?.some((q) => q.commentId === commentId);
  },
}));

/** Convenience selector — pure read of the queue for a conversation. */
export function useReviewQueue(conversationId: string | null): QueuedReviewItem[] {
  return useReviewQueueStore((s) => (conversationId ? s.queuesByConversation[conversationId] ?? [] : []));
}
